/* istanbul ignore file */

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { v4 as uuidv4 } from 'uuid';
import { getLoadingText, getStepFromStreamEvent, LoadingStep } from './streamableHelpers';

const MIN_STEP_MS = 2000;

const delay = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

export const streamTravelWishlist = async ({
  apiUrl,
  conversationId,
  message,
  intl,
  onStepChange,
}) => {
  const controller = new AbortController();
  let settled = false;
  let result = null;
  let streamError = null;

  let currentStep = LoadingStep.FINDING;
  let currentStepStartedAt = Date.now();
  let stepQueue = Promise.resolve();

  let receivedDone = false;

  onStepChange(currentStep, getLoadingText(intl, currentStep));

  try {
    await fetchEventSource(`${apiUrl}/stream`, {
      signal: controller.signal,
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        x_amex_tracking_id: uuidv4(),
        x_conversation_id: conversationId,
      },
      body: JSON.stringify({
        conversation_id: conversationId,
        user_message: message,
      }),

      onopen(response) {
        const contentType = response.headers.get('content-type') || '';

        if (!response.ok || !contentType.includes('text/event-stream')) {
          throw new Error(`Unexpected response: ${response.status} ${contentType}`);
        }
      },

      onmessage(event) {
        const data = JSON.parse(event.data);

        const nextStep = getStepFromStreamEvent(data);

        if (nextStep) {
          stepQueue = stepQueue.then(async () => {
            const elapsed = Date.now() - currentStepStartedAt;
            const waitMs = Math.max(0, MIN_STEP_MS - elapsed);

            if (waitMs > 0) {
              await delay(waitMs);
            }

            currentStep = nextStep;
            currentStepStartedAt = Date.now();
            onStepChange(currentStep, getLoadingText(intl, currentStep));
          });
        }

        if (data.type === 'done') {
          receivedDone = true;

          result = {
            agentMessage: data.agent_message,
            hotels: data.hotels ?? [],
            raw: data,
          };

          stepQueue = stepQueue.then(async () => {
            const elapsed = Date.now() - currentStepStartedAt;
            const waitMs = Math.max(0, MIN_STEP_MS - elapsed);

            if (waitMs > 0) {
              await delay(waitMs);
            }

            settled = true;
            controller.abort();
          });
        }
      },

      onclose() {
        if (!settled && !receivedDone && !streamError) {
          streamError = new Error('Stream closed before done event');
        }
      },

      onerror(err) {
        if (!settled && !streamError) {
          streamError = err;
        }

        throw err;
      },
    });
  } catch (err) {
    if (!(err instanceof DOMException && err.name === 'AbortError') && !streamError) {
      streamError = err;
    }
  }

  await stepQueue;

  if (streamError) {
    throw streamError;
  }

  if (!result) {
    throw new Error('No done event received');
  }

  return result;
};
